import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Modal,
  TextField,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import {
  Search,
  X,
  Plus,
  ChevronDown,
  Trash2,
  FolderSymlink,
} from 'lucide-react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import CategorizedChatList from '../list/CategorizedChatList';
import CategoryManager from '../categories/CategoryManager';
import ProjectManager from '../projects/ProjectManager';
import { ProjectContextProvider } from '../projects/ProjectContextProvider';

// Helper function for colors - Apple palette
const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const appleColors = {
    light: {
      label: { primary: '#000000' },
      system: {
        grey: '#8E8E93',
        grey2: '#AEAEB2',
        grey3: '#C7C7CC',
        grey6: '#F2F2F7',
        inputBg: '#F2F2F7',
        green: '#34C759',
        white: '#FFFFFF',
        black: '#000000'
      },
      bg: {
        primaryElevated: '#FFFFFF',
        secondary: '#F2F2F7',
        tertiary: '#FAFAFA'
      },
      fill: { tertiary: '#767680' },
      ui: { accent: '#007AFF', base: '#F2F2F7' }
    },
    dark: {
      label: { primary: '#FFFFFF' },
      system: {
        grey: '#8E8E93',
        grey2: '#636366',
        grey3: '#464649',
        grey6: '#1C1C1E',
        inputBg: '#1C1C1E',
        green: '#30D158',
        white: '#FFFFFF',
        black: '#000000'
      },
      bg: {
        primaryElevated: '#1C1C1E',
        secondary: '#2C2C2E',
        tertiary: '#3A3A3C'
      },
      fill: { tertiary: '#767680' },
      ui: { accent: '#0A84FF', base: '#3A3A3C' }
    }
  };

  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: alpha(c.system.grey3, 0.3),
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// Mobile-specific styled components matching Settings.js mobile design
const MobileBackdrop = styled(Box)(({ theme, open }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'transparent',
  zIndex: theme.zIndex.modal - 1,
  opacity: open ? 1 : 0,
  transition: 'opacity 250ms ease',
  visibility: open ? 'visible' : 'hidden',
}));

const MobileModalContainer = styled(motion.div)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? '#1C1C1E'
        : '#FFFFFF'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 -2px 20px rgba(0, 0, 0, 0.5)'
      : '0 -2px 20px rgba(0, 0, 0, 0.15)',
    zIndex: theme.zIndex.modal,
    maxHeight: '85vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    touchAction: 'auto',
  };
});

const DragArea = styled(Box)({
  width: '100%',
  touchAction: 'none',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  }
});

const DragHandle = styled(Box)(({ theme }) => ({
  width: '36px',
  height: '5px',
  borderRadius: '3px',
  backgroundColor: theme.palette.mode === 'dark'
    ? '#8E8E93'
    : '#8E8E93',
  opacity: 0.3,
  margin: '12px auto 4px',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
    opacity: 0.5,
  }
}));

const MobileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  padding: '0 16px 10px'
}));

const MobileContent = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'dark'
        ? '#1C1C1E'
        : '#FFFFFF'),
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  };
});

const MobileFooter = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'dark'
        ? '#1C1C1E'
        : '#FFFFFF'),
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    borderTop: `1px solid ${isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)'
      )}`,
    padding: theme.spacing(2, 3, 4),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    zIndex: 10,
    touchAction: 'auto',
  };
});

const SearchContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    padding: '4px 16px 16px',
    borderBottom: `1px solid ${isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)'
      )}`,
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'dark'
        ? '#1C1C1E'
        : '#FFFFFF'),
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };
});

const SearchInputWrapper = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark' ? 'rgba(28, 28, 30, 0.8)' : 'rgba(242, 242, 247, 0.8)'),
    borderRadius: '10px',
    padding: '8px 12px',
  };
});

const BlueButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? '#FFFFFF'
      : (theme.palette.mode === 'dark' ? '#0A84FF' : '#007AFF'),
    color: isWallpaperMode
      ? '#000000'
      : 'white',
    padding: '8px',
    minWidth: '34px',
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.85)'
        : (theme.palette.mode === 'dark' ? '#0071E3' : '#0062CC'),
    },
    '& svg': {
      width: '18px',
      height: '18px',
    },
    transition: 'all 0.25s ease',
  };
});

const MobileNavigationSection = styled(Box)(({ theme }) => {
  return {
    marginBottom: theme.spacing(2),
    backgroundColor: 'transparent',
  };
});

// Styled components for dialog
const MobileDialogTitle = styled(DialogTitle)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontWeight: 600,
    fontSize: '18px',
    color: isWallpaperMode ? '#FFFFFF' : colors.textDefault,
    padding: '20px 20px 12px',
    textAlign: 'center',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '36px',
      height: '3px',
      borderRadius: '1.5px',
      backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.accent,
    }
  };
});

const MobileDialogContent = styled(DialogContent)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: '24px 20px 16px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.background,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
  };
});

const MobileDialogActions = styled(DialogActions)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: '12px 16px 20px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.background,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    justifyContent: 'space-between',
    borderTop: `1px solid ${isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.borderColor}`,
  };
});

const MobileSelect = styled(Select)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '12px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : colors.borderColor,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: isWallpaperMode ? '#FFFFFF' : colors.accent,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: isWallpaperMode ? '#FFFFFF' : colors.accent,
    },
    '& .MuiSelect-select': {
      padding: '14px 16px',
      fontSize: '16px',
      color: isWallpaperMode ? '#FFFFFF' : colors.textDefault,
    }
  };
});

const MobileMenuItem = styled(MenuItem)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    margin: '2px 0',
    color: isWallpaperMode ? '#FFFFFF' : colors.textDefault,
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? alpha('#FFFFFF', 0.1)
        : alpha(colors.accent, 0.08),
    },
    '&.Mui-selected': {
      backgroundColor: isWallpaperMode
        ? alpha('#FFFFFF', 0.15)
        : alpha(colors.accent, 0.12),
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? alpha('#FFFFFF', 0.2)
          : alpha(colors.accent, 0.16),
      }
    }
  };
});

const MobileButton = styled(Button)(({ theme, color = 'primary', fullWidth = false }) => {
  const colors = navigationColors(theme);
  const isCancel = color === 'inherit';
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '12px',
    padding: '10px 16px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    flex: fullWidth ? 1 : 'auto',
    boxShadow: 'none',
    ...(isCancel ? {
      borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : colors.borderColor,
      color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#FF3B30' : '#FF3B30'),
      '&:hover': {
        backgroundColor: 'transparent',
      }
    } : {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.accent,
      color: isWallpaperMode ? '#000000' : '#ffffff',
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.85)'
          : (theme.palette.mode === 'dark' ? '#0071E3' : '#0062CC'),
        boxShadow: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: alpha(isWallpaperMode ? '#FFFFFF' : colors.accent, 0.5),
        color: isWallpaperMode ? '#000000' : '#ffffff',
      }
    })
  };
});

// Mobile Chat Modal Component
const MobileChatModal = ({
  isOpen,
  onClose,
  user,
  createNewChatHandler,
  searchTerm,
  setSearchTerm,
  setIsSearchFocused,
  loadChats,
  selectedCategory,
  setSelectedCategory,
  selectedProject,
  currentChatId,
  filteredChats,
  selectChat,
  handleMenuOpen: externalHandleMenuOpen,
  hasNextPage,
  isNextPageLoading,
  handleSelectProject,
  handleProjectChatSelect,
  persistentChatState,
  setPersistentChatState,
  projectsSectionRef,
  setSnackbarMessage,
  setSnackbarOpen,
  handleDeleteChat,
  updateChatCategory,
  filterCategories,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const colors = navigationColors(theme);

  // State for menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);

  // State for category change dialog
  const [changeCategoryDialogOpen, setChangeCategoryDialogOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');

  // Debounced search function
  const debouncedSearch = useMemo(() =>
    debounce(async (term) => {
      if (!user?.uid) return;

      if (!term.trim()) {
        loadChats(false);
        return;
      }

      loadChats(false);
    }, 300),
    [user?.uid, loadChats]
  );

  const handleSearchChange = useCallback((e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (term.trim()) {
      setIsSearchFocused(true);
    } else {
      setIsSearchFocused(false);
    }

    debouncedSearch(term);
  }, [debouncedSearch, setSearchTerm, setIsSearchFocused]);

  // Format category functions
  const formatCategoryTitle = (category) => {
    if (!category) return t("chatModal.allChats");
    if (category === "uncategorized") return t("chatModal.uncategorized");
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const formatCategoryName = (category) => {
    if (category === "uncategorized") return t("chatModal.uncategorized");
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Framer Motion drag end handler
  const handleDragEnd = (_, info) => {
    if (info.offset.y > window.innerHeight * 0.25) {
      onClose();
    }
  };

  // Menu handlers
  const handleMenuOpen = useCallback((event, chatId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedChatId(chatId);

    if (externalHandleMenuOpen) {
      externalHandleMenuOpen(event, chatId);
    }
  }, [externalHandleMenuOpen]);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleOpenChangeCategoryDialog = useCallback(() => {
    const categories = ['personal', 'work', 'team', 'ideas', 'uncategorized'];

    if (filterCategories && Array.isArray(filterCategories)) {
      filterCategories.forEach(cat => {
        if (!categories.includes(cat)) {
          categories.push(cat);
        }
      });
    }

    setAvailableCategories(categories);
    setChangeCategoryDialogOpen(true);
    handleMenuClose();
  }, [filterCategories, handleMenuClose]);

  const handleChangeCategory = useCallback(() => {
    const chatIdToUpdate = selectedChatId;

    if (!chatIdToUpdate) {
      setSnackbarMessage(t('chatModal.errorNoSelection'));
      setSnackbarOpen(true);
      setChangeCategoryDialogOpen(false);
      setNewCategory('');
      return;
    }

    if (chatIdToUpdate && newCategory) {
      updateChatCategory(chatIdToUpdate, newCategory)
        .then((result) => {
          if (result.success) {
            setSnackbarMessage(t('chatModal.categoryChanged', { category: newCategory }));
            setSnackbarOpen(true);
            loadChats(false);
          } else {
            setSnackbarMessage(result.message || t('chatModal.failedToChangeCategory'));
            setSnackbarOpen(true);
          }
        })
        .catch((error) => {
          setSnackbarMessage(t('chatModal.failedToChangeCategory'));
          setSnackbarOpen(true);
        });
    }

    setChangeCategoryDialogOpen(false);
    setNewCategory('');
  }, [selectedChatId, newCategory, loadChats, setSnackbarMessage, setSnackbarOpen, updateChatCategory, t]);

  const handleDeleteSelectedChat = useCallback(() => {
    if (selectedChatId && handleDeleteChat) {
      handleDeleteChat(selectedChatId)
        .then((result) => {
          if (result.success) {
            setSnackbarMessage(t('chatModal.chatDeleted'));
            setSnackbarOpen(true);
          } else {
            setSnackbarMessage(result.message || t('chatModal.failedToDeleteChat'));
            setSnackbarOpen(true);
          }
        })
        .catch(() => {
          setSnackbarMessage(t('chatModal.errorDeletingChat'));
          setSnackbarOpen(true);
        });
    }
    handleMenuClose();
  }, [selectedChatId, handleDeleteChat, setSnackbarMessage, setSnackbarOpen, handleMenuClose, t]);

  return (
    <>
      <MobileBackdrop open={isOpen} onClick={onClose} />

      <Modal
        open={isOpen}
        onClose={onClose}
        closeAfterTransition
        disableAutoFocus={true}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
        disableScrollLock={false}
        keepMounted={true}
        BackdropProps={{
          invisible: true,
        }}
      >
        <Box>
          <AnimatePresence>
            {isOpen && (
              <MobileModalContainer
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{
                type: 'spring',
                damping: 30,
                stiffness: 300,
                mass: 0.8
              }}
              drag="y"
              dragConstraints={{ top: 0, bottom: window.innerHeight }}
              dragElastic={0.6}
              onDragEnd={handleDragEnd}
              dragDirectionLock
              dragMomentum={true}
              dragTransition={{
                min: 0,
                max: 200,
                power: 0.2
              }}
            >
              <DragArea>
                <DragHandle />

                <MobileHeader>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: '24px',
                      color: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000')
                    }}
                  >
                    {t("chatModal.yourChats")}
                  </Typography>
                  <IconButton
                    onClick={onClose}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      color: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : '#8E8E93'
                    }}
                  >
                    <ChevronDown size={20} />
                  </IconButton>
                </MobileHeader>
              </DragArea>

              {/* Search Header */}
              <SearchContainer>
                <SearchInputWrapper>
                  <Search size={16} color={colors.textSecondary} style={{ marginRight: '8px' }} />
                  <TextField
                    variant="standard"
                    placeholder={t("chatModal.searchChats")}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        color: theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : colors.textDefault
                      }
                    }}
                    sx={{
                      flex: 1,
                      '& input': {
                        padding: 0,
                        fontSize: '15px',
                        color: theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : colors.textDefault,
                      },
                      '& input::placeholder': {
                        color: colors.textSecondary,
                        opacity: 0.7,
                      }
                    }}
                  />
                  {searchTerm && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setIsSearchFocused(false);
                        loadChats(false);
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <X size={14} />
                    </IconButton>
                  )}
                </SearchInputWrapper>

                <BlueButton onClick={() => {
                  createNewChatHandler();
                  onClose();
                }}>
                  <Plus />
                </BlueButton>
              </SearchContainer>

              {/* Main Content */}
              <MobileContent>
                {/* Categories section */}
                <MobileNavigationSection>
                  <CategoryManager
                    data-component="CategoryManager"
                    userId={user?.uid}
                    selectedCategory={selectedCategory}
                    onCategoryChange={(category) => {
                      setSelectedCategory(category);
                      if (category !== 'add') {
                        handleSelectProject(null);
                      }
                    }}
                    onNotification={(notification) => {
                      setSnackbarMessage(notification.message);
                      setSnackbarOpen(true);
                    }}
                  />
                </MobileNavigationSection>

                {/* Projects section */}
                <MobileNavigationSection>
                  <ProjectContextProvider>
                    <ProjectManager
                      data-component="ProjectManager"
                      ref={projectsSectionRef}
                      selectedCategory={selectedCategory}
                      selectedProject={selectedProject}
                      onSelectProject={handleSelectProject}
                      user={user}
                      onProjectChatSelect={handleProjectChatSelect}
                    />
                  </ProjectContextProvider>
                </MobileNavigationSection>

                {/* Chat list section */}
                <MobileNavigationSection sx={{ flexGrow: 1, overflow: 'auto' }}>
                  <ProjectContextProvider>
                    <CategorizedChatList
                      data-component="CategorizedChatList"
                      chats={filteredChats}
                      currentChatId={currentChatId}
                      onSelectChat={(chat) => {
                        selectChat(chat);
                        onClose();
                      }}
                      onMenuOpen={handleMenuOpen}
                      hasNextPage={hasNextPage}
                      isNextPageLoading={isNextPageLoading}
                      onLoadMore={() => loadChats(true)}
                      selectedCategory={selectedCategory}
                      persistentState={persistentChatState}
                      onPersistentStateChange={(newState) => {
                        setPersistentChatState((prev) => ({
                          ...prev,
                          ...newState,
                        }));
                      }}
                      onCreateNewChat={() => {
                        createNewChatHandler();
                        onClose();
                      }}
                    />
                  </ProjectContextProvider>
                </MobileNavigationSection>
              </MobileContent>

              {/* Footer */}
              <MobileFooter>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : colors.textSecondary,
                    fontSize: '13px'
                  }}
                >
                  {t("chatModal.chatsIn", { count: filteredChats?.length || 0, category: selectedCategory ? formatCategoryTitle(selectedCategory).toLowerCase() : t("chatModal.allCategories") })}
                </Typography>
              </MobileFooter>
            </MobileModalContainer>
          )}
          </AnimatePresence>
        </Box>
      </Modal>

      {/* Chat Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: '180px',
            borderRadius: '12px',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.5)'
              : '0 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
          },
        }}
      >
        <MenuItem
          onClick={handleOpenChangeCategoryDialog}
          sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.1),
            }
          }}
        >
          <FolderSymlink
            size={16}
            style={{
              marginRight: '10px',
              color: theme.palette.primary.main
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              color: theme.palette.text.primary
            }}
          >
            {t("chatModal.changeCategory")}
          </Typography>
        </MenuItem>

        <MenuItem
          onClick={handleDeleteSelectedChat}
          sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.15 : 0.1),
            }
          }}
        >
          <Trash2
            size={16}
            style={{
              marginRight: '10px',
              color: theme.palette.error.main
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              color: theme.palette.error.main
            }}
          >
            {t("chatModal.deleteChat")}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Category Change Dialog */}
      <Dialog
        open={changeCategoryDialogOpen}
        onClose={() => setChangeCategoryDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            width: '100%',
            maxWidth: '340px',
            overflow: 'hidden',
            backgroundColor: theme.palette.actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 10px 25px rgba(0, 0, 0, 0.5)'
              : '0 10px 25px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <MobileDialogTitle>
          {t("chatModal.moveChat")}
        </MobileDialogTitle>

        <MobileDialogContent>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="mobile-category-select-label" sx={{
              fontSize: '15px',
              color: theme.palette.actualMode === 'wallpaper'
                ? '#FFFFFF'
                : colors.textSecondary,
            }}>
              {t("chatModal.selectCategory")}
            </InputLabel>
            <MobileSelect
              labelId="mobile-category-select-label"
              id="mobile-category-select"
              value={newCategory}
              label={t("chatModal.selectCategory")}
              onChange={(e) => setNewCategory(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '12px',
                    mt: 1,
                    padding: '6px',
                    maxHeight: '300px',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                      : '0 4px 20px rgba(0, 0, 0, 0.15)',
                  }
                }
              }}
            >
              {availableCategories.map((category) => (
                <MobileMenuItem key={category} value={category}>
                  {formatCategoryName(category)}
                </MobileMenuItem>
              ))}
            </MobileSelect>
          </FormControl>
        </MobileDialogContent>

        <MobileDialogActions>
          <MobileButton
            onClick={() => setChangeCategoryDialogOpen(false)}
            color="inherit"
            variant="outlined"
            fullWidth
          >
            {t("settings.cancel")}
          </MobileButton>
          <MobileButton
            onClick={handleChangeCategory}
            variant="contained"
            disabled={!newCategory}
            fullWidth
          >
            {t("chatModal.move")}
          </MobileButton>
        </MobileDialogActions>
      </Dialog>
    </>
  );
};

export default MobileChatModal;
